// PrestoPlayer.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import PrestoSinglePlayer from '../component/PrestoSinglePlayer.jsx';
import PrestoDualPlayer from '../component/PrestoDualPlayer.jsx';
import { createAesEmbeddedManifestSource } from '../resource/createAesEmbeddedManifestDataUrl.js';
import { LoadMode } from '../resource/playerConfig.js';
import { hasValidSourceUrl, formatSource } from '../resource/playerUtils.js';

const getSourceWithContentUrl = ({ source, contentUrl }) => {
  if (!source || !contentUrl) return source;

  const [primarySource] = formatSource({
    dash: source.dash,
    hls: source.hls,
  });

  if (!primarySource || primarySource.src === contentUrl) return source;

  return {
    ...source,
    [primarySource.typeKey]: contentUrl,
  };
};

/** VOD entry point. Routes to PrestoDualPlayer (splash→main) or PrestoSinglePlayer. */
const PrestoPlayer = ({
  source = null,
  contentUrl = '',
  shouldUseTrailer = false,
  shouldUseSplash = false,
  splashVideo = null,
  keySystem = null,
  robustness = null,
  token = '',
  widevineServerCertificateBase64 = '',
  drmLicenseDuration = 600,
  canViewMedia = false,
  option,
  autoplay,
  isMuted,
  loop = false,
  loadMode = LoadMode.NONE,
  isAesEncrypted = false,
  clientId = '',
  sessionId = '',
  messageId = '',
  onError = null,
  ...otherProps
}) => {
  const isDual = Boolean(shouldUseSplash && hasValidSourceUrl(splashVideo));

  const mergedAutoplay = option?.autoplay ?? autoplay;
  const mergedIsMuted = option?.muted ?? isMuted;
  const mergedLoop = option?.loop ?? loop;

  const playbackSource = useMemo(
    () =>
      shouldUseTrailer && source?.mp4
        ? { mp4: source.mp4 }
        : getSourceWithContentUrl({ source, contentUrl }),
    [contentUrl, shouldUseTrailer, source]
  );

  // Only rewrite AES main manifests. The blurred preview manifest should not go
  // through the embed-key inline flow
  // note: clear /manifest.m3u8  blur: /blurred.m3u8
  const shouldUseAesEmbeddedManifest =
    isAesEncrypted && playbackSource?.hls?.includes('/manifest.m3u8');

  const aesManifestUrl = shouldUseAesEmbeddedManifest
    ? playbackSource?.hls
    : null;
  const [aesResolvedSource, setAesResolvedSource] = useState(null);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const playbackSourceRef = useRef(playbackSource);
  playbackSourceRef.current = playbackSource;

  useEffect(() => {
    if (!aesManifestUrl) {
      setAesResolvedSource(null);
      return undefined;
    }

    let isCancelled = false;

    createAesEmbeddedManifestSource({
      manifestUrl: aesManifestUrl,
      clientId,
      sessionId,
      token,
      messageId,
    })
      .then(({ dataUrl, codecs }) => {
        if (isCancelled) return;
        setAesResolvedSource({
          ...playbackSourceRef.current,
          hls: dataUrl,
          hlsCodecs: codecs,
        });
      })
      .catch(error => {
        if (isCancelled) return;
        onErrorRef.current?.(error);
      });

    return () => {
      isCancelled = true;
    };
  }, [aesManifestUrl, clientId, sessionId, token, messageId]);

  const resolvedSource = shouldUseAesEmbeddedManifest
    ? aesResolvedSource
    : playbackSource;

  const drm = useMemo(
    () =>
      !isAesEncrypted && !shouldUseTrailer && canViewMedia && token && keySystem
        ? {
            keySystem,
            drmToken: token,
            robustness,
            widevineServerCertificateBase64,
            drmLicenseDuration,
          }
        : null,
    [
      isAesEncrypted,
      shouldUseTrailer,
      canViewMedia,
      token,
      keySystem,
      robustness,
      widevineServerCertificateBase64,
      drmLicenseDuration,
    ]
  );

  if (isDual) {
    return (
      <PrestoDualPlayer
        messageId={messageId}
        source={resolvedSource}
        splashVideo={splashVideo}
        drm={drm}
        shouldUseSplash={shouldUseSplash}
        loadMode={loadMode}
        isMuted={mergedIsMuted}
        onError={onError}
        {...otherProps}
      />
    );
  }

  return (
    <PrestoSinglePlayer
      messageId={messageId}
      source={resolvedSource}
      drm={drm}
      isNativePlayback={shouldUseTrailer}
      autoplay={mergedAutoplay}
      isMuted={mergedIsMuted}
      loop={mergedLoop}
      loadMode={loadMode}
      onError={onError}
      {...otherProps}
    />
  );
};

PrestoPlayer.propTypes = {
  source: PropTypes.object,
  contentUrl: PropTypes.string,
  shouldUseTrailer: PropTypes.bool,
  shouldUseSplash: PropTypes.bool,
  splashVideo: PropTypes.object,
  keySystem: PropTypes.string,
  robustness: PropTypes.string,
  token: PropTypes.string,
  widevineServerCertificateBase64: PropTypes.string,
  drmLicenseDuration: PropTypes.number,
  canViewMedia: PropTypes.bool,
  option: PropTypes.object,
  autoplay: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isMuted: PropTypes.bool,
  loop: PropTypes.bool,
  loadMode: PropTypes.string,
  isAesEncrypted: PropTypes.bool,
  clientId: PropTypes.string,
  sessionId: PropTypes.string,
  messageId: PropTypes.string,
  onError: PropTypes.func,
};

export default PrestoPlayer;
